
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { format } from 'date-fns';
import { Building, Euro, ExternalLink, PhoneCall, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { LeadStatusSelect } from './LeadStatusSelect';
import { LeadsTableProps } from './types';
import { useNavigate } from 'react-router-dom';

export function LeadsTableMobile({ leads, onLeadUpdate, filters }: LeadsTableProps) {
  const navigate = useNavigate();
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);

  const handleDeleteLead = async (id: string) => {
    try {
      setIsDeleting(id);
      const { error } = await supabase
        .from('nursing_home_leads')
        .delete()
        .eq('id', id);
        
      if (error) throw error;
      
      toast.success('Lead removed successfully');
      // Call onLeadUpdate to refresh the list after deletion
      onLeadUpdate();
    } catch (error) {
      console.error('Error deleting lead:', error);
      toast.error('Failed to remove lead');
    } finally {
      setIsDeleting(null);
    }
  };

  const handleStatusChange = async (id: string, newStatus: string) => {
    try {
      setUpdatingStatus(id);
      const { error } = await supabase
        .from('nursing_home_leads')
        .update({ status: newStatus })
        .eq('id', id);
        
      if (error) throw error;
      
      toast.success(`Status updated to ${newStatus}`);
      onLeadUpdate();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const filteredLeads = leads.filter(lead => {
    if (filters.status && lead.status !== filters.status) return false;
    if (filters.county && lead.location !== filters.county) return false;
    return true;
  });

  if (filteredLeads.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground bg-gray-50 rounded-lg">
        No nursing home leads found with the current filters
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {filteredLeads.map((lead) => (
        <Card key={lead.id} className="p-4 overflow-hidden">
          <div className="flex items-start justify-between mb-3">
            <Button 
              variant="link" 
              className="flex items-center space-x-2 p-0 h-auto"
              onClick={() => navigate(`/dashboard/leads/${lead.id}`)}
            >
              <div className="w-8 h-8 bg-grandpal-beige rounded-full flex items-center justify-center shrink-0">
                <Building className="h-4 w-4 text-grandpal-green" />
              </div>
              <div>
                <h3 className="font-medium truncate max-w-[200px] text-left">{lead.nursing_home_name}</h3>
                <div className="flex items-center text-xs">
                  <span className={lead.category === 'Private' ? 'text-purple-600' : 'text-blue-600'}>
                    {lead.category || 'Unknown'}
                  </span>
                  <span className="mx-1">•</span>
                  <span className="text-gray-500 capitalize">{lead.location || 'Unknown location'}</span>
                </div>
              </div>
            </Button>
            
            <LeadStatusSelect
              leadId={lead.id}
              status={lead.status || "interested"}
              updatingStatus={updatingStatus}
              onStatusChange={handleStatusChange}
            />
          </div>

          <div className="flex flex-wrap gap-2 mb-3 text-xs">
            <Badge variant="outline" className="font-normal text-gray-500">
              Added {format(new Date(lead.created_at), 'MMM d, yyyy')}
            </Badge>
          </div>
          
          <div className="flex justify-between border-t pt-3">
            <div className="flex space-x-2">
              <Button 
                size="sm" 
                variant="ghost" 
                className="h-8 w-8 p-0"
                onClick={() => window.open(`/nursing-homes/${lead.location?.toLowerCase()}/${lead.nursing_home_id}`, '_blank')}
              >
                <ExternalLink className="h-4 w-4" />
              </Button>
            </div>
            
            <Button 
              size="sm" 
              variant="ghost" 
              className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
              onClick={() => handleDeleteLead(lead.id)}
              disabled={isDeleting === lead.id}
            >
              {isDeleting === lead.id ? (
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-red-500 border-t-transparent" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
            </Button>
          </div>
        </Card>
      ))}
    </div>
  );
}
